import React, { useState } from 'react';
import { Trash2, Loader2, Mail, Download, MapPin, Calendar } from 'lucide-react';
import TestEmailPanel from './TestEmailPanel';
import BouncesPanel from './BouncesPanel';
import RemovalLogCard from './RemovalLogCard';
import ListCleanupCard from './ListCleanupCard';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    timeZone: 'Australia/Melbourne',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const locOf = (sub) =>
  sub.city && sub.country ? `${sub.city}, ${sub.country}` : sub.country || 'Unknown';

const UTM_COLS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'landing_page'];

const hasUtm = (sub) => UTM_COLS.some((k) => sub[k]);

// Campaign attribution, captured from the om_utm cookie at signup.
const UtmLine = ({ sub }) => {
  if (!hasUtm(sub)) return null;
  return (
    <div
      className="mt-2 flex flex-wrap gap-1.5"
      data-testid={`subscriber-utm-${sub.email}`}
    >
      {UTM_COLS.filter((k) => sub[k]).map((k) => (
        <span
          key={k}
          className="rounded bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 text-[10px] text-gray-400"
        >
          <span className="text-gray-600">{k.replace('utm_', '').replace('landing_page', 'landing')}:</span>{' '}
          <span className="text-amber-300/90 break-all">{sub[k]}</span>
        </span>
      ))}
    </div>
  );
};

// ---- Mobile card (used on <sm) ----
const SubscriberCard = ({ sub, onDelete, deleting }) => (
  <div
    className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3"
    data-testid={`subscriber-row-${sub.email}`}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-white text-sm font-black break-all flex-1 min-w-0">{sub.email}</p>
      <button
        type="button"
        onClick={() => onDelete(sub.email)}
        disabled={deleting}
        data-testid={`subscriber-delete-${sub.email}`}
        aria-label={`Delete ${sub.email}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shrink-0"
      >
        {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
        Delete
      </button>
    </div>
    <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500 flex-wrap">
      <span className="inline-flex items-center gap-1">
        <Calendar className="h-2.5 w-2.5" /> {fmtDate(sub.subscribed_at)}
      </span>
      <span className="inline-flex items-center gap-1">
        <MapPin className="h-2.5 w-2.5" /> {locOf(sub)}
      </span>
      {sub.ip && (
        <span className="font-mono normal-case tracking-normal text-gray-600">{sub.ip}</span>
      )}
    </div>
    <UtmLine sub={sub} />
  </div>
);

// ---- Desktop table row (used on ≥sm) ----
const SubscriberRow = ({ sub, onDelete, deleting }) => (
  <tr className="border-b border-red-900/10 hover:bg-zinc-800/50" data-testid={`subscriber-row-desktop-${sub.email}`}>
    <td className="py-3 px-4 text-white break-all">{sub.email}</td>
    <td className="py-3 px-4 text-gray-400 text-sm whitespace-nowrap">{fmtDate(sub.subscribed_at)}</td>
    <td className="py-3 px-4 text-gray-400 text-sm">{locOf(sub)}</td>
    <td className="py-3 px-4 text-gray-400 text-sm font-mono">{sub.ip || 'N/A'}</td>
    <td className="py-3 px-4 text-amber-300/90 text-xs break-all" data-testid={`subscriber-utm-source-${sub.email}`}>{sub.utm_source || '—'}</td>
    <td className="py-3 px-4 text-gray-400 text-xs break-all">{sub.utm_medium || '—'}</td>
    <td className="py-3 px-4 text-gray-400 text-xs break-all">{sub.utm_campaign || '—'}</td>
    <td className="py-3 px-4 text-gray-400 text-xs break-all" data-testid={`subscriber-utm-content-${sub.email}`}>{sub.utm_content || '—'}</td>
    <td className="py-3 px-4 text-gray-500 text-xs break-all">{sub.landing_page || '—'}</td>
    <td className="py-3 px-4 text-right">
      <button
        type="button"
        onClick={() => onDelete(sub.email)}
        disabled={deleting}
        aria-label={`Delete ${sub.email}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
      >
        {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
        Delete
      </button>
    </td>
  </tr>
);

const SubscribersTab = ({ subscribers, onExportCSV, onRefresh, token }) => {
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter?\n\nThey'll stop receiving story emails. You can't undo this - they'd have to re-subscribe.`)) return;
    setDeletingEmail(email);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/newsletter/subscribers/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      onRefresh?.();
    } catch (e) {
      setError(e.message || 'Delete failed.');
    } finally {
      setDeletingEmail(null);
    }
  };

  const filtered = query.trim()
    ? subscribers.filter((s) => (s.email || '').toLowerCase().includes(query.trim().toLowerCase()))
    : subscribers;

  return (
    <div data-testid="subscribers-tab">
      <TestEmailPanel token={token} />
      <BouncesPanel token={token} />
      <div className="mb-4">
        <ListCleanupCard token={token} onChanged={onRefresh} />
      </div>
      <div className="mb-4">
        <RemovalLogCard token={token} />
      </div>
      <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-red-500" />
            <h3 className="text-lg sm:text-xl font-black text-white">
              Subscribers ({subscribers.length})
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by email…"
              data-testid="subscribers-search"
              className="bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-1.5 text-sm text-white outline-none flex-1 sm:flex-none sm:w-48 min-w-0"
            />
            <button
              onClick={onExportCSV}
              data-testid="export-csv-btn"
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-3 rounded text-xs uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 shrink-0"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-950/40 border border-red-500/50 text-red-300 px-3 py-2 rounded text-sm mb-3">
            {error}
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="py-8 text-center text-gray-500 text-sm">
            {query ? `No subscribers match "${query}".` : 'No subscribers yet.'}
          </p>
        ) : (
          <>
            {/* Mobile: card-per-row so nothing gets clipped by the tab width */}
            <div className="sm:hidden space-y-2">
              {filtered.map((sub) => (
                <SubscriberCard
                  key={sub.email + sub.subscribed_at}
                  sub={sub}
                  onDelete={handleDelete}
                  deleting={deletingEmail === sub.email}
                />
              ))}
            </div>

            {/* Desktop: full-width table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-red-900/30">
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Email</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Subscribed</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Location</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">IP</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">UTM Source</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Medium</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Campaign</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Content</th>
                    <th className="py-3 px-4 text-gray-400 font-bold text-xs uppercase tracking-widest">Landing</th>
                    <th className="py-3 px-4 text-right text-gray-400 font-bold text-xs uppercase tracking-widest">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((sub) => (
                    <SubscriberRow
                      key={sub.email + sub.subscribed_at}
                      sub={sub}
                      onDelete={handleDelete}
                      deleting={deletingEmail === sub.email}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscribersTab;
